import { memo, useMemo } from "react";
import type { AirDashStation, FallbackTier } from "@chula/shared";
import { aqiStyle } from "../lib/aqiColor";
import { PanelHeader } from "./PanelHeader";
import { PanelState, listFeedState } from "./PanelState";

interface Props {
  stations: AirDashStation[];
  loading?: boolean;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
  onFlyTo?: (lng: number, lat: number) => void;
  /** Refetch the feed — shown as Retry when the source is offline. */
  onRetry?: () => void;
}

const MAX_ROWS = 5;

/**
 * AIRDASH · PM2.5 — nearest Air4Thai stations relayed by the AirDash sister
 * system (this Mac, :8341). Real regulator readings around campus, ranked by
 * distance; complements the single-point AQI badge above it.
 */
export const AirDashPanel = memo(function AirDashPanel({
  stations,
  loading,
  ageMinutes,
  fallbackTier,
  onFlyTo,
  onRetry,
}: Props) {
  const rows = useMemo(
    () =>
      [...stations]
        .sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity))
        .slice(0, MAX_ROWS),
    [stations],
  );

  const state = listFeedState({ loading, count: rows.length, tier: fallbackTier, ageMinutes });

  if (state === "loading" || state === "empty" || state === "error") {
    return (
      <div className="left-section airdash-panel">
        <PanelHeader title="AIRDASH · PM2.5" ageMinutes={ageMinutes} fallbackTier={fallbackTier ?? null} source="airdash" />
        <PanelState kind={state} what="air stations" source="AirDash (Air4Thai)" ageMinutes={ageMinutes} onRetry={onRetry} />
      </div>
    );
  }

  return (
    <div className="left-section airdash-panel">
      <PanelHeader title="AIRDASH · PM2.5" ageMinutes={ageMinutes} fallbackTier={fallbackTier ?? null} source="airdash" />
      {state === "stale" && <PanelState kind="stale" what="station readings" ageMinutes={ageMinutes} />}
      <div className="flood-list">
        <ul>
          {rows.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                className="flood-list-btn"
                onClick={() => onFlyTo?.(s.lng, s.lat)}
                title={`${s.name}${s.distanceKm != null ? ` · ${s.distanceKm} km` : ""}${s.aqi != null ? ` · AQI ${s.aqi}` : ""}`}
              >
                <span className="flood-list-lvl mono" style={aqiStyle(s.aqi)}>
                  {s.pm25 != null ? s.pm25.toFixed(0) : "—"}
                </span>
                <span className="flood-list-name">{s.name}</span>
                <span className="flood-list-trend mono">
                  {s.distanceKm != null ? `${s.distanceKm} km` : "µg/m³"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <span className="caption mono" style={{ color: "var(--ink-low)" }}>
        Air4Thai via AirDash · µg/m³ · nearest first
      </span>
    </div>
  );
});
